/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';

import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiDotsHorizontal } from '@lumapps/lumx/icons';
import { Placement, Popover, Theme } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { ItemVariant, ViewMode } from '../../constants';
import { CLASSNAME as ITEM_CLASSNAME, NavigationItem, NavigationItemProps } from '../NavigationItem';
import './index.scss';

export interface NavigationProps {
    /** Content of the navigation. These components should be of type NavigationItem to be rendered */
    children?: React.ReactNode;
    /** Classname that will be used for the nav wrapping element */
    className?: string;
    /** ARIA role for the wrapping nav element */
    role?: React.AriaRole;
    /** props override for the "More" children items */
    moreChildrenProps?: Partial<NavigationItemProps>;
    /** props override for the "More" navigation item */
    moreItemProps?: Partial<NavigationItemProps>;
    /** the scope used to create data attributes */
    scope?: string;
    /** Theme that will be applied to the element, either Theme.dark or Theme.light */
    theme?: Theme;
    /** The view mode that will be used to display the nav, either 'horizontal' or 'vertical', defaults to 'vertical' */
    viewMode?: ViewMode;
}

const CLASSNAME = 'navigation';
const MORE_INDEX = 'more';

/**
 * Reusable component for displaying a non-editable navigation.
 *
 * @family Layouts
 * @param NavigationProps
 * @returns Navigation
 */
export const Navigation: React.FunctionComponent<NavigationProps> = ({
    children,
    className,
    moreChildrenProps,
    moreItemProps,
    scope,
    theme,
    viewMode,
    ...navigationForwardedProps
}) => {
    const childrenArray = React.Children.toArray(children);
    const { get } = useDataAttributes(scope || CLASSNAME);
    const { block } = useClassnames(CLASSNAME);
    const { block: itemBlock, element: itemElement } = useClassnames(ITEM_CLASSNAME);
    const isHorizontalViewMode = viewMode === ViewMode.horizontal;
    const isDarkTheme = theme === Theme.dark;

    const { translate } = useTranslate();

    const [visibilityMap, setVisibilityMap] = React.useState<Record<string, boolean>>({});
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [ulElement, setUlElement] = React.useState<HTMLUListElement | null>(null);
    const ulRef = React.useRef<HTMLUListElement>(null);
    const moreRef = React.useRef<HTMLLIElement>(null);

    const [widthMap, setWidthMap] = React.useState<Record<string, number>>({});

    React.useLayoutEffect(() => {
        const nextWidthMap: Record<string, number> =
            !isHorizontalViewMode || !ulRef.current
                ? {}
                : (Array.from(ulRef.current.children) as HTMLElement[]).reduce(
                      (acc, item) => ({ ...acc, [item.dataset.itemIndex as string]: item.offsetWidth }),
                      {},
                  );

        // keeps a stable children width map over time (stable unless one of them changes width)
        if (!isEqual(widthMap, nextWidthMap)) {
            setWidthMap(nextWidthMap);
        }

        // builds a stable state for the resize observer (see regular useEffect)
        if (ulRef.current !== ulElement) {
            setUlElement(ulRef.current);
        }
    });

    const handleResize = React.useMemo(() => {
        if (!isHorizontalViewMode || !ulRef.current) {
            return null;
        }

        let prevWidth = 0;

        return throttle(() => {
            let availableWidth = (ulRef.current as HTMLUListElement).clientWidth;

            // we don't want to execute the code if the width has not changed
            if (availableWidth === prevWidth) {
                return;
            }
            prevWidth = availableWidth;

            const { columnGap } = getComputedStyle(ulRef.current as HTMLUListElement);
            const columnGapWidth = parseInt(columnGap, 10) || 0;

            const newVisibilityMap: Record<string, boolean> = {};

            Object.keys(widthMap)
                .filter((key) => key !== MORE_INDEX)
                .map((key) => parseInt(key, 10))
                .sort((a, b) => a - b)
                .forEach((key, index) => {
                    const itemsCount = Object.keys(widthMap).length - 1;

                    const itemWidth = widthMap[key];
                    const moreWidth = widthMap[MORE_INDEX];

                    const isItemVisible =
                        index < itemsCount - 1
                            ? availableWidth - (itemWidth + columnGapWidth + moreWidth) >= 0
                            : availableWidth - itemWidth >= 0;

                    newVisibilityMap[key] = isItemVisible;
                    newVisibilityMap[MORE_INDEX] = !isItemVisible;

                    availableWidth = isItemVisible ? availableWidth - (itemWidth + columnGapWidth) : 0;
                });

            setVisibilityMap((prevVisibilityMap) =>
                isEqual(prevVisibilityMap, newVisibilityMap) ? prevVisibilityMap : newVisibilityMap,
            );
            setIsPopoverOpen(false);
        }, 100);
    }, [isHorizontalViewMode, widthMap]);

    React.useEffect(() => {
        if (!isHorizontalViewMode || !handleResize || !ulElement) {
            return undefined;
        }

        handleResize();

        const observer = new ResizeObserver(handleResize);

        observer.observe(ulElement);

        return () => observer.unobserve(ulElement);
    }, [isHorizontalViewMode, handleResize, ulElement]);

    return (
        <nav className={className} {...navigationForwardedProps}>
            <ul
                className={block({
                    dark: isDarkTheme,
                    horizontal: isHorizontalViewMode,
                })}
                ref={ulRef}
            >
                {childrenArray.map((child, index) =>
                    // istanbul ignore next
                    React.isValidElement(child)
                        ? React.cloneElement(child as React.ReactElement, {
                              className: itemBlock(
                                  { hidden: isHorizontalViewMode && !visibilityMap[index] },
                                  child?.props?.className,
                              ),
                              'data-item-index': index,
                              scope,
                              theme,
                              viewMode,
                          })
                        : null,
                )}
                {isHorizontalViewMode ? (
                    <NavigationItem
                        aria-controls="more-popover"
                        aria-expanded={isPopoverOpen}
                        scope={scope}
                        element="item-more"
                        label={translate(GLOBAL.MORE)}
                        icon={mdiDotsHorizontal}
                        className={itemElement('more', {
                            hidden: !visibilityMap[MORE_INDEX],
                        })}
                        data-item-index={MORE_INDEX}
                        ref={moreRef}
                        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                        theme={theme}
                        isSectionOpen={isPopoverOpen}
                        {...moreItemProps}
                    />
                ) : null}
            </ul>
            {isHorizontalViewMode ? (
                <Popover
                    id="more-popover"
                    anchorRef={moreRef}
                    isOpen={isPopoverOpen}
                    placement={Placement.BOTTOM_START}
                    usePortal={false}
                    closeOnClickAway
                    closeOnEscape
                    onClick={() => setIsPopoverOpen(false)}
                    onClose={() => setIsPopoverOpen(false)}
                    onKeyUp={(event: React.KeyboardEvent) => {
                        if (event.key === 'Enter') {
                            setIsPopoverOpen(false);
                        }
                    }}
                    zIndex={996}
                    {...get({ element: 'more-popover' })}
                >
                    <ul className={block({ popover: true })}>
                        {childrenArray.map((child, index) =>
                            !visibilityMap[index] && React.isValidElement(child)
                                ? React.cloneElement(child as React.ReactElement, {
                                      variant: ItemVariant.default,
                                      ...moreChildrenProps,
                                      scope,
                                      theme: Theme.light,
                                      viewMode: ViewMode.vertical,
                                  })
                                : null,
                        )}
                    </ul>
                </Popover>
            ) : null}
        </nav>
    );
};
Navigation.displayName = 'Navigation';
